import type { FieldRendererProps, FieldDisplayProps } from '../../config/types';
import { Label } from '@/components/ui/Label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/Select';
import { Text } from '@/components/ui/Text';
import { useSupportedDefinitions, type JurisdictionValue } from '@/components/engagements/use-supported-definitions';

const EMPTY: JurisdictionValue = { countryCode: '', entityType: '' };

/**
 * Custom field — engagement jurisdiction (ENT-284 merged engagement-create flow).
 * Country + Entity-type selectors where entity types are *dependent* on the chosen
 * country. Encapsulates the dependent population + reset (changing country resets
 * the entity type to that country's first option). Value is a {@link JurisdictionValue}.
 * Only backend-supported (country, entityType) combos are offered.
 */
export function EngagementJurisdictionField({ value, onChange }: FieldRendererProps) {
  const { countries, entityTypesFor } = useSupportedDefinitions();
  const v = (value as JurisdictionValue | undefined) ?? EMPTY;
  const entityTypeOptions = entityTypesFor(v.countryCode);

  const onCountry = (countryCode: string) => {
    // Reset the entity type to the new country's first option (dependent population).
    onChange({ countryCode, entityType: entityTypesFor(countryCode)[0]?.value ?? '' });
  };
  const onEntityType = (entityType: string) => onChange({ ...v, entityType });

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label htmlFor="ej-country">Country</Label>
        <Select value={v.countryCode || undefined} onValueChange={onCountry}>
          <SelectTrigger variant="form" id="ej-country">
            <SelectValue placeholder="Select a country…" />
          </SelectTrigger>
          <SelectContent>
            {countries.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {c.flag ? `${c.flag} ${c.label}` : c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ej-entityType">Entity type</Label>
        <Select
          value={v.entityType || undefined}
          onValueChange={onEntityType}
          disabled={entityTypeOptions.length === 0}
        >
          <SelectTrigger variant="form" id="ej-entityType">
            <SelectValue placeholder="Select an entity type…" />
          </SelectTrigger>
          <SelectContent>
            {entityTypeOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

/** Review-step display for the jurisdiction selection. */
export function EngagementJurisdictionDisplay({ value }: FieldDisplayProps) {
  const v = value as JurisdictionValue | undefined;
  return <Text variant="body-sm" as="span">{v?.countryCode ? `${v.countryCode} · ${v.entityType}` : '—'}</Text>;
}
